import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useListMemberships } from 'src/features/membership/api/useListMemberships';
import CreateMembershipFormModal from 'src/features/membership/components/forms/CreateMembershipFormModal';
import { CellAllowedDays } from 'src/features/membership/components/cell/CellAllowedDays';
import CellMembershipActions from 'src/features/membership/components/cell/CellMembershipActions';
import { Membership } from 'src/features/membership/types';
import { ALL_CATEGORIES } from 'src/features/membership/utils/constants';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { membershipTypeBadgeOptions } from 'src/features/membership/utils/constants';
import { MEMBERSHIP_TEMPLATE_CATEGORY } from 'src/features/membershipType/types';
import { PATH } from 'src/features/navigation/utils/constants';
import { Translation } from 'src/types';
import Tab from 'src/registry/components/navigation/Tab';
import TabRow from 'src/registry/components/navigation/TabRow';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { generalMessages } from 'src/registry/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import { booleanBadgeOptions } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import useSearch from 'src/registry/hooks/useSearch';
import usePagination from 'src/registry/hooks/usePagination';
import { useListMembershipTypes } from 'src/features/membershipType/api/useListMembershipTypes';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import { CellLink } from 'src/registry/components/table/cells/CellLink';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';

const columnHelper = createColumnHelper<Membership>();

const translate = (translations: Translation[], locale: string) => {
    if (!translations) return '-';
    const localeTranslation = translations.find((translation) => translation.language === locale)?.name;
    return localeTranslation ?? '-';
};

type MembershipProps = {
    entityId?: string;
};

export default function MembershipsTable({ entityId }: MembershipProps) {
    const { formatMessage, locale } = useIntl();
    const manageMembershipsPermission = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIPS]);
    const accessMembershipTemplate = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIP_TYPES]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { queryFiltersOptions, ...filters } = useFilters();

    const [searchMembershipType, setSearchMembershipType] = React.useState<string>('');

    const { category = '' } = useParams();

    const isAllCategories = React.useMemo(() => category === ALL_CATEGORIES, [category]);

    const { parkingId } = useParking();

    //Do not show membership category tabs if on entity dashboard page
    const showMembershipTabRows = entityId === undefined;

    const { setTitle, removeTitle } = useTitleStore();
    const { data, isLoading } = useListMemberships({
        parkingId,
        ...(entityId ? { owner: entityId } : {}),
        ...(!isAllCategories ? { category } : {}),
        ...querySearchOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
    });

    //Reset filters and search when category tab changes
    React.useEffect(() => {
        filters.setColumnFiltersShowDropdownState([]);
        filters.setColumnFiltersState([]);
        search.setGlobalFilterState('');
    }, [category]);

    const { data: listMembershipTypes } = useListMembershipTypes({
        parkingId,
        search: searchMembershipType,
        config: { enabled: filters.columnFiltersShowDropdownState.includes('membership_type') },
    });

    const membershipTypesFilterOptions = React.useMemo(
        () =>
            listMembershipTypes?.results?.map((type) => {
                const translatedType = type.translations.find((t) => t.language === locale);

                return { value: type.id, label: translatedType?.name };
            }),
        [listMembershipTypes?.results, locale],
    );

    const effectiveCategory = entityId ? ALL_CATEGORIES : category || ALL_CATEGORIES;

    React.useEffect(() => {
        setTitle(formatMessage(membershipMessages.memberships));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const [defaultDateFilter] = React.useState(() => {
        const now = Date.now();

        return {
            afterDate: new Date(now - 30 * 24 * 60 * 60 * 1000),
            beforeDate: new Date(),
        };
    });

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: (ctx) => {
                return (
                    <CellLink
                        to={`/${PATH.MEMBERSHIPS}/${effectiveCategory}/${ctx.row.original.id}/${ctx.row.original.membership_type.id}`}
                        {...ctx}
                    />
                );
            },
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.accessor('membership_type.is_active', {
            header: DataTableColumnHeader,
            enableColumnFilter: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(membershipMessages.isActive),
                badgeOptions: booleanBadgeOptions,
            },
            id: 'is_active',
        }),
        columnHelper.accessor('owner.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(membershipMessages.owner),
            },
        }),
        columnHelper.accessor('membership_type.category', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: isAllCategories,
            cell: CellBadge,
            meta: {
                title: formatMessage(membershipMessages.type),
                badgeOptions: membershipTypeBadgeOptions,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
            id: 'category',
        }),
        columnHelper.accessor('membership_type.id', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableColumnFilter: true,
            cell: (props) => {
                const membershipType = props.row.original.membership_type;
                if (!membershipType) return '-';
                const value = translate(membershipType.translations, locale);
                return (
                    <LinkWithIcon
                        to={`/${PATH.ADMINISTRATION}/membership-templates/${membershipType.id}`}
                        linkTitle={value}
                        hasPermissions={accessMembershipTemplate}
                    />
                );
            },
            meta: {
                title: formatMessage(membershipMessages.template),
                options: membershipTypesFilterOptions,
                inputType: INPUT_TYPE.SEARCH,
            },
            id: 'membership_type',
        }),
        columnHelper.accessor('in_parking', {
            header: DataTableColumnHeader,
            cell: CellBoolean,
            meta: {
                title: formatMessage(membershipMessages.inParking),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        columnHelper.accessor('max_allowed_spaces_count', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(membershipMessages.allowedSpaces),
            },
        }),
        columnHelper.accessor('membership_identifiers_count', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(membershipMessages.identifiers),
            },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(membershipMessages.activeFrom),
                inputType: INPUT_TYPE.DATE,
                customDropdownMenuLabel: formatMessage(membershipMessages.activePeriod),
                filterLabel: formatMessage(membershipMessages.activePeriod),
                dateFilter: defaultDateFilter,
                inlineDateFilter: true,
            },
            id: 'started',
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(membershipMessages.activeTo),
            },
        }),
        columnHelper.accessor('membership_allowed_times', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellAllowedDays,
            meta: {
                title: formatMessage(membershipMessages.allowedDays),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: (props) => <CellMembershipActions {...props} category={category} />,
        }),
    ];
    return (
        <>
            {showMembershipTabRows && (
                <TabRow>
                    <Tab end to={`/${PATH.MEMBERSHIPS}/${ALL_CATEGORIES}`}>
                        {formatMessage(membershipMessages.memberships)}
                    </Tab>
                    {Object.values(MEMBERSHIP_TEMPLATE_CATEGORY).map((type) => (
                        <Tab to={`/${PATH.MEMBERSHIPS}/${type}`} key={type}>
                            {formatMessage(membershipTypeBadgeOptions[type].label)}
                        </Tab>
                    ))}
                </TabRow>
            )}
            <DataTable<Membership, any>
                isLoading={isLoading}
                columns={columns}
                data={data}
                search={search}
                filters={filters}
                pagination={pagination}
                semantics={{
                    title: formatMessage(membershipMessages.memberships),
                    entryName: formatMessage(membershipMessages.membership).toLowerCase(),
                    entryNames: formatMessage(membershipMessages.memberships).toLowerCase(),
                    placeholder: formatMessage(membershipMessages.membership),
                }}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                    newEntry:
                        isAllCategories || entityId || !manageMembershipsPermission
                            ? undefined
                            : CreateMembershipFormModal,
                }}
                onFilterSearch={(columnId, value) => {
                    if (columnId === 'membership_type') {
                        setSearchMembershipType(value);
                    }
                }}
            />
        </>
    );
}
